import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ReferenceMeasureConfirmModalComponent } from "@components/reference-measure-confirm-modal/reference-measure-confirm-modal.component";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { FormDirective } from "@odx/angular/components/form-field";
import { ModalService } from "@odx/angular/components/modal";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { ReferenceMeasureService } from "@project/floorplanner/floorplan-additional-notes/reference-measure-notes/reference-measure.service";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-reference-measure-notes",
  imports: [FloorplanItemDetailsComponent, FormDirective, UiKitModule, ReactiveFormsModule],
  templateUrl: "./reference-measure-notes.component.html",
  standalone: true,
})
export class ReferenceMeasureNotesComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private referenceMeasureService: ReferenceMeasureService = inject(ReferenceMeasureService);
  private readonly modalService = inject(ModalService);

  @Input({ required: true }) set floorplanItem(floorplanItem: FloorplanItem) {
    this.referenceMeasure = floorplanItem as ReferenceMeasure;
  }

  protected referenceMeasureForm!: FormGroup;
  protected referenceMeasure!: ReferenceMeasure;
  private previousValue!: number;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.initForm();

    this.referenceMeasure.lengthChanged.pipe(takeUntil(this.destroy$)).subscribe(({ currentData, pendingData }) => {
      this.modalService
        .open(ReferenceMeasureConfirmModalComponent, {
          dismissable: false,
        })
        .onClose$.subscribe((isConfirmed: boolean) => {
          if (isConfirmed) {
            this.referenceMeasure.updateForm(
              pendingData.width,
              pendingData.height,
              pendingData.rotation,
              pendingData.x,
              pendingData.y,
            );
          } else {
            this.referenceMeasure.resetForm(currentData);
          }
        });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.previousValue = this.referenceMeasure.scale;
    this.referenceMeasureForm = this.formBuilder.group({
      text: [this.referenceMeasure.scale, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)]],
    });
  }

  onTextBlur() {
    const control = this.referenceMeasureForm.get("text");
    if (!control || !control.valid) return;

    const newValue = +control.value;

    if (newValue === this.previousValue) return;

    this.openModal(control, newValue);
  }

  private openModal(control: any, newValue: number) {
    this.modalService
      .open(ReferenceMeasureConfirmModalComponent, {
        dismissable: false,
      })
      .onClose$.subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.referenceMeasure.scale = newValue;
          this.previousValue = newValue;
          this.referenceMeasureService.refreshReferenceMeasure();
        } else {
          control.setValue(this.previousValue, { emitEvent: false });
        }
      });
  }
}
